/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { Dropdown, DropdownItemProps, Grid, Segment } from 'semantic-ui-react';

import {
  ConfirmModal,
  Notification,
  POSButton,
  POSLoadingOverlay,
  showNotification,
  useHttp,
} from '@publicisgroupe/p-os-components';

import { webframeApi } from 'api/webframeApi';
import DropzoneContainer from 'components/DropzoneContainer';
import LayoutHeader from 'components/LayoutHeader';
import { useUserContext } from 'contexts/AuthProvider';
import { UpdateClientProfileDto, CreateClientProfileDto, UpdateFileDto } from 'types/types';
import { getOptionsForLevel } from 'utils/helpers';
import { ROUTE_FRAME_THEMES } from 'utils/routes';

import './ThemeCustomizationPage.less';
import { ClientProfile, ClientProfileImageFileConstraints, ClientProfileFileKeys } from './types';
import {
  FOOTER_IMAGE_CONSTRAINTS,
  FOOTER_LOGO_CONSTRAINTS,
  HEADER_IMAGE_CONSTRAINTS,
  HEADER_LOGO_CONSTRAINTS,
} from './constants/constraints';
import { PROFILE_FILE_KEYS } from './constants/constants';
import { isEqual } from 'lodash';
import useErrorNotification from 'hooks/useErrorNotification';

type LocationState = {
  clientName?: string;
  isEditMode?: boolean;
  clientProfileId?: string;
};

export const ThemeCustomizationPage = () => {
  const { t } = useTranslation();
  const http = useHttp();
  const { navData } = useUserContext();
  const history = useHistory();

  const location = useLocation<LocationState>();
  const { clientName, isEditMode, clientProfileId } = location.state ?? ({} as LocationState);

  const initialClientProfileState = { client: clientName };

  const [initialClientProfile, setInitialClientProfile] =
    useState<ClientProfile>(initialClientProfileState);
  const [currentProfile, setCurrentProfile] = useState<ClientProfile>(initialClientProfileState);
  const [clientsDropdownData, setClientsDropdownData] = useState<DropdownItemProps[]>([]);
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState(false);
  const [openQuitModal, setOpenQuitModal] = useState<boolean>(false);
  const { showErrorNotification } = useErrorNotification();

  const getApi = useCallback(() => {
    const api = webframeApi(http);
    if (!api) {
      throw new Error('Could not get webframe API');
    }
    return api;
  }, [http]);

  const getDropdownData = useCallback(
    (clientsWithProfile?: string[]): DropdownItemProps[] => {
      const filterNames = navData?.[0].filters ?? [];
      let allClientsFromFilters = navData ?? [];

      if (clientsWithProfile && !isEditMode) {
        allClientsFromFilters = allClientsFromFilters.filter(
          (obj) => !clientsWithProfile.includes(obj.name)
        );
      }

      return getOptionsForLevel(0, allClientsFromFilters, filterNames, [], false);
    },
    [isEditMode, navData]
  );

  useEffect(() => {
    if (!(isEditMode && clientProfileId)) {
      return;
    }

    const getClientProfile = async () => {
      setIsLoading(true);

      try {
        const { data } = await getApi().getClientProfile(clientProfileId);

        const getFile = (storageName?: string) =>
          storageName ? { existingFileName: storageName } : undefined;

        const clientProfile: ClientProfile = {
          id: data.id,
          client: data.client,
          footerLogo: getFile(data.footerLogoStorageName),
          footerImage: getFile(data.footerImageStorageName),
          headerImage: getFile(data.headerImageStorageName),
          headerLogo: getFile(data.headerLogoStorageName),
        };

        setInitialClientProfile(clientProfile);
        setCurrentProfile(clientProfile);
      } catch (e) {
        showErrorNotification();
      } finally {
        setIsLoading(false);
      }
    };

    getClientProfile();
  }, [isEditMode, clientProfileId, getApi, showErrorNotification]);

  const handleClientChange = (clientName: string) => {
    setCurrentProfile((prevState) => ({
      ...prevState,
      client: clientName,
    }));
  };

  const createClientProfile = async (clientProfileRequest: CreateClientProfileDto) => {
    try {
      await getApi().createClientProfile(clientProfileRequest);

      showNotification.success(successNotification);
    } catch (e: any) {
      showErrorNotification({ content: e?.response?.data });
    } finally {
      setIsLoading(false);
    }
  };

  const updateClientProfileAsync = async (clientProfileUpdateRequest: UpdateClientProfileDto) => {
    try {
      await getApi().updateClientProfile(clientProfileUpdateRequest);
      showNotification.success(successNotification);
      history.push(ROUTE_FRAME_THEMES);
    } catch (e: any) {
      showErrorNotification();
    }
  };

  useEffect(() => {
    if (clientsDropdownData.length > 1) {
      return;
    }

    if (isEditMode) {
      setClientsDropdownData(getDropdownData());
      return;
    }

    const getClientsData = async () => {
      setIsLoading(true);

      try {
        const { data: clientsWithProfile } = await getApi().getAllClientsWithProfile();

        setClientsDropdownData(getDropdownData(clientsWithProfile));
      } catch (e: any) {
        showErrorNotification({ content: e?.response?.data });
      } finally {
        setIsLoading(false);
      }
    };

    getClientsData();
  }, [
    isEditMode,
    navData,
    getApi,
    getDropdownData,
    showErrorNotification,
    clientsDropdownData.length,
  ]);

  const handleSaveClientProfile = async () => {
    setIsLoading(true);

    try {
      if (isEditMode) {
        const updateProfile: UpdateClientProfileDto = {
          id: clientProfileId!,
          footerImage: mergeDataForUpdate('footerImage'),
          footerLogo: mergeDataForUpdate('footerLogo'),
          headerLogo: mergeDataForUpdate('headerLogo'),
          headerImage: mergeDataForUpdate('headerImage'),
        };

        await updateClientProfileAsync(updateProfile);
      } else {
        const createClientProfileRequest: CreateClientProfileDto = {
          client: currentProfile.client!,
          footerImage: currentProfile['footerImage']?.data,
          footerLogo: currentProfile['footerLogo']?.data,
          headerLogo: currentProfile['headerLogo']?.data,
          headerImage: currentProfile['headerImage']?.data,
        };

        await createClientProfile(createClientProfileRequest);
        history.push(ROUTE_FRAME_THEMES);
      }
    } catch (e) {
      showErrorNotification();
    } finally {
      setIsLoading(false);
    }
  };

  const mergeDataForUpdate = (
    profileFileType: ClientProfileFileKeys
  ): UpdateFileDto | undefined => {
    let removed = false;

    const currentFile = currentProfile[profileFileType];
    const initialFile = initialClientProfile[profileFileType];

    const fileNotExisted = !currentFile && !initialFile;
    const fileNotTouched =
      currentFile?.existingFileName &&
      currentFile?.existingFileName === initialFile?.existingFileName;

    if (fileNotExisted || fileNotTouched) {
      return undefined;
    }
    if (!currentFile && initialFile?.existingFileName) {
      removed = true;
    }

    return {
      removed: removed,
      file: currentFile?.data,
    } as UpdateFileDto;
  };

  useEffect(() => {
    const hasFiles = (profile: ClientProfile): boolean =>
      PROFILE_FILE_KEYS.some((key) => profile[key]);

    const thereIsNoChanges = isEqual(initialClientProfile, currentProfile);
    setIsSaveButtonDisabled(
      !hasFiles(currentProfile) || !currentProfile.client || thereIsNoChanges
    );
  }, [currentProfile, initialClientProfile]);

  const handleAddFile = (files: File[], constraints: ClientProfileImageFileConstraints) => {
    setCurrentProfile((prevState) => ({
      ...prevState,
      [constraints.name]: {
        data: files[0],
      },
    }));
  };

  const getEmptyFile = (fileName: string): File => {
    return new File([], fileName, { type: 'text/plain' });
  };

  const handleDeleteFile = (file: File, constraints: ClientProfileImageFileConstraints) => {
    const currentFile = currentProfile[constraints.name];

    if (!currentFile) return;

    setCurrentProfile((prevState) => ({
      ...prevState,
      [constraints.name]: undefined,
    }));
  };

  const confirmQuit = () => {
    setCurrentProfile(initialClientProfile);
    history.push(ROUTE_FRAME_THEMES);
    setOpenQuitModal(false);
  };

  const handleQuitButtonClick = () => {
    setOpenQuitModal(true);
  };

  const successNotification: Notification = {
    title: 'Success',
    content: t('ClientProfileSuccessMessage'),
    inverted: true,
  };

  const renderSaveButton = () => (
    <POSButton onClick={handleSaveClientProfile} disabled={isSaveButtonDisabled}>
      {t('Save')}
    </POSButton>
  );

  const renderClientDropdown = () => (
    <Grid.Row>
      {isSaveButtonDisabled && (
        <span className="profile-creation-content-special-note">
          {t('ThemeCustomizationValidationMessage')}
        </span>
      )}
      <Grid.Column width={8}>
        <Segment>
          <p
            className="profile-creation-content-header"
            data-testid="profile-creation-content-header"
          >
            {t('ThemeCustomizationSelectClient')}
            <span className="red">*</span>
          </p>
          <Dropdown
            className="profile-creation-content-client-selection"
            data-testid="profile-creation-content-client-selection"
            fluid
            selection
            options={clientsDropdownData}
            onChange={(_event, data) => handleClientChange(data.value?.toString() ?? '')}
            defaultValue={currentProfile.client ?? ''}
            multiple={false}
            disabled={isEditMode}
          />
        </Segment>
      </Grid.Column>
    </Grid.Row>
  );

  const renderContentSection = (title: string) => (
    <Grid.Row columns={1} className="profile-creation-content-section">
      <Grid.Column>
        <p
          className="profile-creation-content-upload-section-header"
          data-testid="profile-creation-content-upload-section-header"
        >
          {title}
        </p>
      </Grid.Column>
    </Grid.Row>
  );

  const renderUploadSection = (title: string, constraints: ClientProfileImageFileConstraints) => {
    const profileFile = currentProfile[constraints.name];
    let file = undefined;
    if (profileFile?.data) {
      file = profileFile.data;
    } else if (profileFile?.existingFileName) {
      file = getEmptyFile(profileFile.existingFileName);
    }

    return (
      <Grid.Column>
        <p
          className="profile-creation-content-upload-section-subheader"
          data-testid="profile-creation-content-upload-section-subheader"
        >
          {title}
        </p>
        <DropzoneContainer
          constraints={constraints}
          files={file ? [file] : undefined}
          handleDeleteFile={handleDeleteFile}
          handleAddFile={handleAddFile}
        />
      </Grid.Column>
    );
  };

  return (
    <POSLoadingOverlay loading={isLoading}>
      <div className="profile-creation" data-testid="profile-creation">
        <LayoutHeader
          breadcrumbs={[]}
          title={t('ThemeCustomizationPageTitle')}
          ctaContent={renderSaveButton()}
        />

        <main style={{ display: 'flex', flexDirection: 'column' }}>
          <div className="profile-creation-content" data-testid="profile-creation-content">
            <Grid>
              {renderClientDropdown()}

              {renderContentSection(t('ThemeCustomizationSectionAddLogos'))}

              <Grid.Row columns={2} className="profile-creation-content-upload-section">
                {renderUploadSection(t('ThemeCustomizationHeaderLogo'), HEADER_LOGO_CONSTRAINTS)}
                {renderUploadSection(t('ThemeCustomizationFooterLogo'), FOOTER_LOGO_CONSTRAINTS)}
              </Grid.Row>

              {renderContentSection(t('ThemeCustomizationSectionAddImages'))}

              <Grid.Row columns={2} className="profile-creation-content-upload-section">
                {renderUploadSection(t('ThemeCustomizationHeaderImage'), HEADER_IMAGE_CONSTRAINTS)}
                {renderUploadSection(t('ThemeCustomizationFooterImage'), FOOTER_IMAGE_CONSTRAINTS)}
              </Grid.Row>
            </Grid>
          </div>

          <div className="profile-creation-footer" data-testid="profile-creation-footer">
            {renderSaveButton()}
            <POSButton inverted onClick={handleQuitButtonClick}>
              {t('Quit')}
            </POSButton>
          </div>
          <ConfirmModal
            openConfirmModal={openQuitModal}
            headerContent={t('QuitConfirmation')}
            subHeaderContent={''}
            primaryButtonText={t('Ok')}
            secondaryButtonText={t('Cancel')}
            setOpenConfirmModal={setOpenQuitModal}
            onConfirm={confirmQuit}
          />
        </main>
      </div>
    </POSLoadingOverlay>
  );
};
